<script setup>
import { ref, onMounted, onBeforeMount, watch } from 'vue';
import { useAuthStore } from "@stores/auth";
import { useAlert } from "@stores/alert";
import { useFooterStore } from '@stores/footer';
import ApiService from "@services/ApiService";

const authStore = useAuthStore();
const isLoggedIn = authStore.isLoggedIn;
const footerStore = useFooterStore();
const { showAlert } = useAlert();

const showModal = ref(false);
const loading = ref(false);
const contentLoading = ref(true);
const currentRegulation = ref();
const termsAccepted = ref(false);


const loadRegulationContent = async () => {
    contentLoading.value = true;
    try {
        if (footerStore.state.regulation === null) {
            await footerStore.store();
        }
        currentRegulation.value = footerStore.state.regulation['terms'];
    } catch (error) {
        console.error('Error loading regulation content:', error);
        showAlert("error", "Erro ao carregar os termos!");
    } finally {
        contentLoading.value = false;
    }
};


const handleAcceptTerms = async () => {
    if (!authStore.isLoggedIn) {
        showModal.value = false;
        return;
    }

    loading.value = true;
    try {
        const response = await ApiService.post('/api/user-settings/accept-terms', {
            user_id: authStore.user?.id,
            accepted_terms: true,
        });

        if (response && response.data) {
            showAlert("success", "Termos aceitos com sucesso!");
            showModal.value = false;
            termsAccepted.value = true;
        }
    } catch (error) {
        console.error('Error accepting terms:', error);
        showAlert("error", "Erro ao aceitar os termos, tente novamente!");

        showModal.value = false;
        termsAccepted.value = true;
    } finally {
        loading.value = false;
    }
};

const isTermsAccepted = async () => {
    try {
        const response = await ApiService.get('/api/user-settings/check-terms');
        if (response && response.data) {
            termsAccepted.value = response.data.accepted;
        }
    } catch (error) {
        console.error('Error checking terms:', error);
    }
};

watch(
    () => [authStore.user, authStore.isLoggedIn],
    async ([newUser, newIsLoggedIn]) => {
        if (newUser && newIsLoggedIn) {
            await loadRegulationContent();
            await isTermsAccepted();
            showModal.value = !termsAccepted.value;
        }
    }
);
onBeforeMount(async () => {
    await loadRegulationContent();
});

onMounted(async () => {
    if (import.meta.client && authStore.user) {
        await loadRegulationContent();
        await isTermsAccepted();
        showModal.value = !termsAccepted.value;
    }
});

const hasBeenCheckedThisSession = () => {
  return localStorage.getItem('userAccepted') === 'true';
};

watch(
    () => [authStore.user, authStore.isLoggedIn],
    async ([newUser, newIsLoggedIn]) => {
        if (newUser && newIsLoggedIn && !termsAccepted.value) {
            await loadRegulationContent();
            showModal.value = true;
        }
    }
);

</script>

<template>
    <BaseModal v-if="false">
        <section class="scrollbar-custom relative flex h-full select-none flex-col bg-brand-background xl:rounded-md">
            <div class="w-full p-3 text-white">
                <h2 class="text-lg font-bold">Atualização dos nossos Termos e Condições.</h2>

                <p class="text-xs mt-4 text-gray-400">
                    Atualizamos nossos Termos e Condições. Você pode conferi-los abaixo:
                </p>
                <div class="bg-brand-card p-4 rounded-md my-3">
                    <div class="prose prose-invert max-h-[35vh] overflow-y-auto mb-6">
                        <div v-if="contentLoading" class="flex items-center justify-center p-4">
                            <Icon name="fa6-solid:circle-notch" class="animate-spin text-2xl" />
                            <span class="ml-2">Carregando termos...</span>
                        </div>
                        <div v-else-if="currentRegulation?.text" v-html="currentRegulation.text" class="terms-content">
                        </div>
                        <div v-else class="text-center p-4">
                            <p>Erro ao carregar os termos. Por favor, tente novamente mais tarde.</p>
                        </div>
                    </div>
                </div>

                <button :disabled="loading" @click="handleAcceptTerms"
                    class="h-12 w-full rounded bg-brand-primary py-2 text-white hover:bg-brighten-brand-primary disabled:opacity-50">
                    <span v-if="!loading">Aceito os Termos</span>
                    <span v-else class="inline-flex items-center gap-2">
                        <span>Processando</span>
                        <Icon name="fa6-solid:circle-notch" class="animate-spin" />
                    </span>
                </button>
            </div>
        </section>
    </BaseModal>
</template>

<style>
.scrollbar-custom {
    scrollbar-width: thin;
    scrollbar-color: #6b7280 transparent;
}

.scrollbar-custom::-webkit-scrollbar {
    width: 8px;
    background-color: transparent;
}

.scrollbar-custom::-webkit-scrollbar-thumb {
    background-color: #6b7280;
    border-radius: 4px;
}

.scrollbar-custom::-webkit-scrollbar-thumb:hover {
    background-color: #4b5563;
}

.scrollbar-custom::-webkit-scrollbar-track {
    background-color: transparent;
}
</style>